// src/lib/capture/finalizeUpload.ts — CBAM mobile capture (Phase 1)
//
// Thin client wrapper over the finalize-upload edge function. Called once the
// resumable upload for a source has completed; the function verifies the
// uploaded object and (subject to the consent guard) enqueues the processing
// job.

import { supabase } from "@/integrations/supabase/client";

export interface FinalizeUploadOpts {
  /** uuid of the research_sources row whose upload just completed. */
  sourceId: string;
  /** Client capture uuid, for cross-checking the uploaded object path. */
  captureUuid?: string;
}

export interface FinalizeUploadResult {
  /** Whether finalization succeeded. */
  ok: boolean;
  /** Whether a downstream processing job was enqueued. */
  jobEnqueued: boolean;
}

/**
 * Finalize a completed upload and (if allowed) enqueue processing. Throws on
 * edge-function/transport error.
 */
export async function finalizeUpload(
  opts: FinalizeUploadOpts,
): Promise<FinalizeUploadResult> {
  const { sourceId, captureUuid } = opts;

  const { data, error } = await supabase.functions.invoke("finalize-upload", {
    body: {
      source_id: sourceId,
      capture_uuid: captureUuid,
    },
  });

  if (error) {
    throw new Error(`finalizeUpload failed: ${error.message}`);
  }

  return {
    ok: Boolean(data?.ok),
    jobEnqueued: Boolean(data?.job_enqueued),
  };
}
